// Phase 26D.2 — Reusable UOM selector (PCS / KG / BAG / BUNCH).
// Reads `sku.allowed_uoms` (default ["PCS"]) and disables units that aren't
// enabled. Used across secondary orders, primary orders, and fulfillment.
//
// Deliberately dependency-free (no shadcn Select) so it can sit inline in a
// line-item row on a mobile screen without opening a portal.

export const ALL_UOMS = ["PCS", "KG", "BAG", "BUNCH"];
// Order-entry screens (primary/secondary order) no longer offer PCS as a
// pickable unit — only Kg/Bag/Bunch. PCS stays available everywhere else
// (SKU master, fulfillment, reports) since it's still the canonical unit.
const ORDER_ENTRY_UOMS = ALL_UOMS.filter(u => u !== "PCS");

export function allowedUomsFor(sku, { excludePcs = false } = {}) {
  const allowed = Array.isArray(sku?.allowed_uoms) && sku.allowed_uoms.length
    ? sku.allowed_uoms
    : ["PCS"];
  const universe = excludePcs ? ORDER_ENTRY_UOMS : ALL_UOMS;
  // Preserve ALL_UOMS ordering.
  return universe.filter(u => allowed.includes(u));
}

export function defaultUomFor(sku, { excludePcs = false } = {}) {
  const list = allowedUomsFor(sku, { excludePcs });
  return list[0] || (excludePcs ? "KG" : "PCS");
}

export default function UomSelector({
  sku, value, onChange, testId, size = "sm", disabled = false, excludePcs = false,
}) {
  const allowed = allowedUomsFor(sku, { excludePcs });
  const universe = excludePcs ? ORDER_ENTRY_UOMS : ALL_UOMS;
  return (
    <div
      className="inline-flex rounded-md border border-neutral-200 bg-neutral-100 p-0.5"
      data-testid={testId || "uom-selector"}
    >
      {universe.map(u => {
        const active = value === u;
        const isAllowed = allowed.includes(u);
        return (
          <button
            key={u}
            type="button"
            disabled={disabled || !isAllowed}
            onClick={() => isAllowed && onChange(u)}
            data-testid={`${testId || "uom-selector"}-${u}`}
            data-active={active ? "true" : "false"}
            className={
              (size === "sm" ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs") +
              " font-semibold uppercase tracking-wider rounded transition-colors " +
              (active ? "bg-[#6D2C91] text-white" : "text-neutral-600 hover:text-neutral-900") +
              (!isAllowed || disabled ? " opacity-40 cursor-not-allowed" : "")
            }
          >
            {u}
          </button>
        );
      })}
    </div>
  );
}
